import {
  isTemplateCreateOperation,
  type TemplateCreateOperation,
  type TemplateHideOperation,
  type TemplateInOut,
} from '@festina/configuration-general-types/configuration'
import { resolveTopologicalPathForBlock } from '@festina-life/api'
import { withLifeErrorBoundary } from '@festina-life/components'
import {
  assertNotEmpty,
  dateUtils,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import {
  FormikCheckbox,
  FormikDateInput,
  FormikForm,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { Checkbox } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useMetaModelContext } from '../../context/metaModelContext'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { useLazyFetchMetaModel } from '../../uiModels/api/metaModel/useLazyFetchMetaModel'
import { stringToDateTimeNullSafe } from '../../uiModels/helpers'
import {
  getTemplateOperationAtDate,
  getTemplateOperationFromDate,
  getTemplateOperationName,
} from '../../uiModels/templateOperations/helpers'
import type { TemplateOperation } from '../../uiModels/templateOperations/types'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { DraftCreationFormId } from '../draftCreationModal/DraftCreationModal'
import { draftCreationStatusReporter } from '../draftCreationModal/draftCreationStatusReporter'
import type {
  DraftCreatedResult,
  DraftCreationProps,
} from '../draftCreationModal/types'
import { DraftPathSelector } from '../draftLocationSelectors/DraftPathSelector'
import { useIsViewDateActiveForOperation } from '../moveViewDate/useIsViewDateActiveForOperation'
import { getTemplateType, validateKey } from './helpers'
import { TemplateModelTypesInput } from './TemplateModelTypesInput'
import type { CreateTemplateDraftInput } from './types'
import { useTemplateDraftValidationSchema } from './useTemplateDraftValidationSchema'

const MoveViewAtInputId = 'moveViewAtDate'

const TemplateDraftCreationFormInternal = ({
  onStatusChanged,
  initialValue,
  layer,
  block,
}: DraftCreationProps<'template'>) => {
  const { onAddOrUpdateOperation, templateOperations } = useOperationsContext()
  const { applicationTemplates: applicationTemplateOperations } =
    useApplicationViewOperationsContext()
  const { viewAt, onViewAtChanged } = useConfigurationContext()
  const translate = useTranslate()
  const { blockTopology } = useMetaModelContext()
  const translateLayer = useTranslateLayer()
  const { mutateAsync: lazyFetchMetaModel } = useLazyFetchMetaModel()

  const checkIsViewDateActiveForTemplateOperation =
    useIsViewDateActiveForOperation<TemplateOperation>({
      operationInterval: (operation) => ({
        from: stringToDateTimeNullSafe(
          getTemplateOperationFromDate(operation),
          Number.NEGATIVE_INFINITY,
        ),
      }),
    })
  const operationsForTemplateId = useCallback(
    (templateId: Optional<string>) => {
      const blockPath = resolveTopologicalPathForBlock(block, blockTopology)
      return templateId
        ? blockPath
            .flatMap((block) => {
              const layeredOperationsInBlock = templateOperations.get(block)
              return layeredOperationsInBlock
                ?.get(layer)
                ?.get(templateId)
                ?.operations.map(({ internalOperation }) => internalOperation)
            })
            .filter(notEmpty)
        : undefined
    },
    [templateOperations, blockTopology, layer, block],
  )
  const initialPackagePath = initialValue?.value?.packagePath ?? []
  const initialValuesWithServerDates = {
    templateId:
      initialValue?.value?.variant === 'version'
        ? (initialValue?.value?.templateId ?? '')
        : '',
    fromDate: initialValue?.value?.fromDate
      ? dateUtils.dateAsServerStr(initialValue.value.fromDate)
      : undefined,
    modelTypeName: initialValue?.value?.modelTypeName ?? '',
    packagePath: initialPackagePath,
    isHideOperation: false,
    variant: initialValue?.value?.variant,
  } satisfies CreateTemplateDraftInput
  const validationSchema = useTemplateDraftValidationSchema({
    layer,
    block,
  })

  const [shouldMoveDate, setShouldMoveDate] = useState(
    !checkIsViewDateActiveForTemplateOperation(
      dateUtils.dateAsServerStr(initialValue?.value?.fromDate),
      operationsForTemplateId(initialValue?.value?.templateId) ?? [],
    ),
  )

  const isTiedToCurrentModel = !!initialValue?.value?.modelTypeName

  // If an initial value has been provided the copied operation must come from the initial value. If the user has changed
  // the template ID we will be making a copy, with a different name, of the initial templateID
  const copiedTemplateIndexedOperations = initialValue?.value?.templateId
    ? applicationTemplateOperations?.get(initialValue.value.templateId)
    : null

  const copiedTemplateOperationAtDate = getTemplateOperationAtDate(
    copiedTemplateIndexedOperations,
    viewAt,
  )

  const copiedTemplateOperationEntity =
    copiedTemplateOperationAtDate &&
    isTemplateCreateOperation(copiedTemplateOperationAtDate.internalOperation)
      ? copiedTemplateOperationAtDate.internalOperation.entity
      : undefined

  const handleSubmit = async (
    input: CreateTemplateDraftInput,
  ): Promise<DraftCreatedResult> => {
    let templateDate = undefined

    if (input.fromDate) {
      templateDate = dateUtils.dateUtcAsLocal(input.fromDate).toDate()
    }

    let templateOperation: TemplateCreateOperation | TemplateHideOperation
    if (input.isHideOperation) {
      templateOperation = {
        _type: 'TemplateHideOperation',
        fromDate: input.fromDate,
        name: input.templateId,
        packageName: input.packagePath.join('.'),
      } satisfies TemplateHideOperation
    } else {
      assertNotEmpty(input.modelTypeName)

      const metaModel = await lazyFetchMetaModel(layer.layerId)
      const templateType = getTemplateType(input.modelTypeName, metaModel)
      const isLibrary = !!templateType?.stereoTypes.includes('IsLibrary')

      templateOperation = {
        _type: 'TemplateCreateOperation',
        entity: {
          ...(copiedTemplateOperationEntity ?? {}),
          _type: isLibrary ? 'LibraryInOut' : 'TemplateInOut',
          templateTypeName:
            templateType?.typeName ??
            copiedTemplateOperationEntity?.templateTypeName,
          modelTypeName: input.modelTypeName,
          packageName: input.packagePath.join('.'),
          templateFromDate: dateUtils.dateAsServerStr(templateDate),
          name: input.templateId,
        } satisfies TemplateInOut,
      } satisfies TemplateCreateOperation
    }

    const createdOnWorkspaceKey = await onAddOrUpdateOperation({
      operation: templateOperation,
      layer,
      block,
      shouldOpenUnifiedBranchWorkspace: true,
    })
    const allOperationsForId =
      operationsForTemplateId(getTemplateOperationName(templateOperation)) ?? []
    const isTemplateDateActive = checkIsViewDateActiveForTemplateOperation(
      getTemplateOperationFromDate(templateOperation),
      allOperationsForId,
    )

    if (!isTemplateDateActive && shouldMoveDate) {
      const firstNonEmptyOperationDate = allOperationsForId
        ?.map((operation) => getTemplateOperationFromDate(operation))
        .find(notEmpty)

      onViewAtChanged(
        templateDate
          ? dateUtils.dateUtcAsLocal(templateDate).toDate()
          : // Given that if there's no fromDate and the param is not active then there must be a version with a fromDate in the middle of the beginning of time and the viewAt date
            dayjs(firstNonEmptyOperationDate!).subtract(1, 'day').toDate(),
      )
    }

    return {
      createdDraft: {
        name: input.templateId,
        fromDate: templateDate,
        type: 'template',
        layer,
        block,
      },
      addedToWorkspaceKey: createdOnWorkspaceKey,
    }
  }

  return (
    <FormikForm
      formId={DraftCreationFormId}
      initialValues={initialValuesWithServerDates}
      onSubmit={draftCreationStatusReporter(onStatusChanged, handleSubmit)}
      validationSchema={validationSchema}
    >
      {({ values }) => {
        const isCopyingTemplateFromDifferentLayer =
          !!copiedTemplateOperationAtDate &&
          !copiedTemplateOperationAtDate.layer.equals(layer)

        // TODO: create a block selector in the future
        const isTemplateDateActiveOnCurrentLayer =
          checkIsViewDateActiveForTemplateOperation(
            values.fromDate,
            operationsForTemplateId(values.templateId),
          )

        return (
          <>
            <FormikCheckbox
              name={validateKey('isHideOperation')}
              label={translate('templateDraftForm.isHideOperation.label')}
              description={translate(
                'templateDraftForm.isHideOperation.description',
              )}
            />
            {!values.isHideOperation && (
              <TemplateModelTypesInput
                layerId={layer.layerId}
                isDisabled={isTiedToCurrentModel}
              />
            )}
            <FormikTextInput
              label={translate('templateDraftForm.templateId.label')}
              name={validateKey('templateId')}
              disabled={initialValue?.value?.variant === 'version'}
            />
            <FormikDateInput
              name={validateKey('fromDate')}
              label={translate('templateDraftForm.fromDate.label')}
              isRequired={false}
            />
            {/* TODO: Add support for editing the parent template, copied template or the template model */}
            {!isTemplateDateActiveOnCurrentLayer && (
              <div className="mt-4">
                <Checkbox
                  wrapperProps={{
                    label: translate('templateDraftForm.moveViewAtLabel'),
                    description: translate(
                      'templateDraftForm.moveViewAtDescription',
                    ),
                  }}
                  id={MoveViewAtInputId}
                  checked={shouldMoveDate}
                  onChange={setShouldMoveDate}
                />
              </div>
            )}
            {isCopyingTemplateFromDifferentLayer &&
              initialValue?.value?.templateId &&
              initialValue?.value?.variant === 'copy' && (
                <Alert severity="warning" className="my-2">
                  {translate('templateDraftForm.warning.differentLayer', {
                    templateId: initialValue?.value?.templateId,
                    sourceLayer: translateLayer(
                      copiedTemplateOperationAtDate.layer,
                    ),
                    targetLayer: translateLayer(layer),
                  })}
                </Alert>
              )}
            <DraftPathSelector layer={layer} block={block} />
          </>
        )
      }}
    </FormikForm>
  )
}

export const TemplateDraftCreationForm = withLifeErrorBoundary(
  withSuspense(TemplateDraftCreationFormInternal),
)
